import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatApiError } from '../../lib/utils';
import { WhatsappLogo } from '@phosphor-icons/react';

export const WHATSAPP_NUDGE_REASONS = new Set([
  'Not Interested',
  'Never Call Again',
  'Language Barrier',
  'Not Ready — Too Early',
  'Investing with Other Broker',
  'Investing Through Bank',
  'Already Has Advisor',
]);

/**
 * Shows a WhatsApp message preview after a lead is closed with a soft-closure reason
 * and lets the agent send the templated nudge via MSG91.
 *
 *  Props:
 *   open, onOpenChange (controlled)
 *   leadId, leadName, leadPhone   identification of the lead
 *   closeReason  the reason just selected (must be in WHATSAPP_NUDGE_REASONS)
 */
export default function WhatsAppNudgeDialog({ open, onOpenChange, leadId, leadName, leadPhone, closeReason }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!open || !leadId || !closeReason) return undefined;
    setLoading(true);
    setError('');
    setPreview(null);
    api
      .get(`/leads/${leadId}/whatsapp-nudge-preview`, { params: { close_reason: closeReason } })
      .then((res) => {
        if (!cancelled) setPreview(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, leadId, closeReason]);

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post(`/leads/${leadId}/whatsapp-nudge`, { close_reason: closeReason });
      toast.success(`WhatsApp message sent to ${leadName || leadPhone}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="whatsapp-nudge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" className="text-emerald-600" /> Send a polite follow-up?
          </DialogTitle>
          <DialogDescription>
            Lead closed as <strong>{closeReason}</strong>. Sending this template keeps the door open without pressure.
            <span className="block text-zinc-500 mt-1">
              To: <strong>{leadName || 'Lead'}</strong> · <span className="tabular-nums">{leadPhone}</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {loading && <Skeleton className="h-48 w-full" />}
          {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3">{error}</p>}
          {preview && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">WhatsApp preview</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{preview.template_name}</span>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-zinc-800" data-testid="whatsapp-preview-body">
{preview.body_preview}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="whatsapp-nudge-skip">
            Skip
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || loading || !preview || !preview.available}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="whatsapp-nudge-send"
          >
            {sending ? 'Sending…' : 'Send WhatsApp'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
